import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCommodities } from '../../store/commoditiesSlice';
// import { addCommodity, updateBidAskToApi } from '../../store/commoditiesSlice';
import { fetchActiveCommodities, addCommodityToApi, updateBidAskToApi } from '../api';

const CommodityForm = () => {
  const dispatch = useDispatch();
  const commodities = useSelector(selectCommodities);
  const [form, setForm] = useState({
    name: '',
    symbol: '',
    contractMonth: '',
    contractYear: '',
    size: '',
    expiryDate: '',
    bid: '',
    ask: ''
  });
  const [error, setError] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const [editBid, setEditBid] = useState('');
  const [editAsk, setEditAsk] = useState('');

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const getAPI = async (dispatch) => {
    await fetchActiveCommodities(dispatch);
  }

  useEffect(() => {
    getAPI(dispatch);
  }, [dispatch]);
  
  const callAPI = async (commodity) => {
    await addCommodityToApi(commodity);
    await fetchActiveCommodities(dispatch);
  }

  const handleSubmit = e => {
    e.preventDefault();
    if (!form.name || !form.symbol || !form.contractMonth || !form.contractYear || !form.size || !form.expiryDate) {
      setError('All fields are required.');
      return;
    }
    setError('');
    callAPI(form, dispatch);
  };

  const updateAPI = async (symbol) => {
    // await addCommodityToApi(commodity);
    await updateBidAskToApi(symbol, Number(editBid), Number(editAsk));
    await fetchActiveCommodities(dispatch);
  }

  const startEdit = (idx, bid, ask) => {
    setEditIdx(idx);
    setEditBid(bid?.toString() ?? '');
    setEditAsk(ask?.toString() ?? '');
  };

  const saveEdit = async (symbol) => {
    updateAPI(symbol);
    setEditIdx(null);
    setEditBid('');
    setEditAsk('');
  };

  // Helper to format price: always keeps at least 2 decimals, removes only extra trailing zeroes
  function formatPrice(value) {
    if (typeof value !== 'number') return value;
    let str = value.toFixed(6); // allow up to 6 decimals for input
    // Remove only extra trailing zeroes after the last non-zero digit, but keep at least 2 decimals
    str = str.replace(/(\.\d*?[1-9])0+$/, '$1'); // e.g. 1.230000 -> 1.23
    if (/\.\d$/.test(str)) str += '0'; // ensure at least 2 decimals
    if (/\.\d{2}$/.test(str)) return str;
    // If ends with .00, keep .00
    if (/\.00$/.test(str)) return str;
    // If ends with .0, add one zero
    if (/\.0$/.test(str)) return str + '0';
    return str;
  }

  // Example options for name and size
  const nameOptions = ['Gold', 'Silver', 'Platinum', 'Copper', 'Wheat', 'Corn', 'Soybeans', 'Brent Crude Oil', 'Natural Gas', 'Coffee', 'Sugar', 'Cocoa', 'Palm Oil', 'Cotton', 'Rice', 'Lumber', 'Palladium', 'Aluminum', 'Zinc', 'Nickel', 'Lead', 'Tin', 'Oats', 'Barley', 'Rapeseed', 'Soybean Meal', 'Soybean Oil'];
  const sizeOptions = ['1 Ounce', '1 Barrel', '1 Bushel', '1 Kilogram', '1 Litre', '1 Pound'];
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  const years = Array.from({ length: 10 }, (_, i) => String(new Date().getFullYear() + i));

  // Sort commodities by expiryDate ascending
  const sortedCommodities = [...commodities].sort((a, b) => {
    if (!a.expiryDate) return 1;
    if (!b.expiryDate) return -1;
    return new Date(a.expiryDate) - new Date(b.expiryDate);
  });

  return (
    <>
      <div style={{
        maxWidth: 600,
        margin: '32px auto',
        borderRadius: 10,
        background: '#f8fafd',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}>
        <h4 style={{ marginBottom: 12, color: '#388e3c' }}>Commodities List</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#f8fafd' }}>
          <thead>
            <tr style={{ background: '#e3eaf2' }}>
              <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left' }}>Name</th>
              <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left' }}>Symbol</th>
              <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left' }}>Contract</th>
              <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left' }}>Size</th>
              <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left' }}>Expiry Date</th>
              <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left' }}>Bid</th>
              <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left' }}>Ask</th>
              <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {commodities.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 8, textAlign: 'center', color: '#888' }}>No commodities found.</td></tr>
            ) : (
              sortedCommodities.map((c, idx) => (
                <tr key={idx}>
                  <td style={{ padding: 8, border: '1px solid #ddd' }}>{c.name}</td>
                  <td style={{ padding: 8, border: '1px solid #ddd' }}>{c.symbol}</td>
                  <td style={{ padding: 8, border: '1px solid #ddd' }}>{`${c?.contractMonth} ${c?.contractYear}`}</td>
                  <td style={{ padding: 8, border: '1px solid #ddd' }}>{c.size}</td>
                  <td style={{ padding: 8, border: '1px solid #ddd' }}>{c.expiryDate}</td>
                  <td style={{ padding: 8, border: '1px solid #ddd' }}>
                    {editIdx === idx ? (
                      <input type="number" value={editBid} onChange={e => setEditBid(e.target.value)} style={{ width: 70 }} />
                    ) : (
                      formatPrice(c.bid)
                    )}
                  </td>
                  <td style={{ padding: 8, border: '1px solid #ddd' }}>
                    {editIdx === idx ? (
                      <input type="number" value={editAsk} onChange={e => setEditAsk(e.target.value)} style={{ width: 70 }} />
                    ) : (
                      formatPrice(c.ask)
                    )}
                  </td>
                  <td style={{ padding: 8, border: '1px solid #ddd' }}>
                    {editIdx === idx ? (
                      <>
                        <button onClick={() => saveEdit(c.symbol)} style={{ marginRight: 8, background: '#388e3c', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: 'pointer' }}>Save</button>
                        <button onClick={() => setEditIdx(null)} style={{ background: '#d32f2f', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: 'pointer' }}>Cancel</button>
                      </>
                    ) : (
                      <button onClick={() => startEdit(idx, c.bid, c.ask)} style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: 'pointer' }}>Edit</button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <form onSubmit={handleSubmit} style={{
        background: '#f8fafd',
        borderRadius: 10,
        padding: 24,
        maxWidth: 400,
        margin: '24px auto'
      }}>
        <h3 style={{ marginBottom: 16, color: '#1976d2' }}>Add Commodity</h3>
        <div style={{ marginBottom: 12 }}>
          <label>Name:</label><br />
          <select name="name" value={form.name} onChange={handleChange} style={{ width: '100%', padding: 8 }}>
            <option value="">Select Name</option>
            {nameOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>Symbol:</label><br />
          <input name="symbol" value={form.symbol} onChange={handleChange} style={{ width: '100%', padding: 8 }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>Contract:</label>
          <select
            value={form.contractMonth || ''}
            onChange={e => setForm({ ...form, contractMonth: e.target.value })}
            style={{ marginRight: 8 }}
          >
            <option value="">Month</option>
            {months.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <select
            value={form.contractYear || ''}
            onChange={e => setForm({ ...form, contractYear: e.target.value })}
          >
            <option value="">Year</option>
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>Size:</label><br />
          <select name="size" value={form.size} onChange={handleChange} style={{ width: '100%', padding: 8 }}>
            <option value="">Select Size</option>
            {sizeOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>Expiry Date:</label><br />
          <input name="expiryDate" value={form.expiryDate} onChange={handleChange} style={{ width: '100%', padding: 8 }} type="date" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>Bid:</label><br />
          <input name="bid" value={form.bid} onChange={handleChange} style={{ width: '100%', padding: 8 }} type="number" step="0.01" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>Ask:</label><br />
          <input name="ask" value={form.ask} onChange={handleChange} style={{ width: '100%', padding: 8 }} type="number" step="0.01" />
        </div>
        {error && <div style={{ color: '#d32f2f', marginBottom: 12 }}>{error}</div>}
        <button type="submit" style={{ background: '#388e3c', color: '#fff', padding: '8px 20px', border: 'none', borderRadius: 6, fontWeight: 500, cursor: 'pointer' }}>Add Commodity</button>
      </form>
    </>
  );
};

export default CommodityForm;
